<script setup lang="ts">
import { ref, computed, onMounted, watch } from "vue";
import { querySchema, formatDecimal, type Field } from "@yawhub/core";
import { AddIcon, PlayIcon, BellIcon } from "../icons";
import { api, errorText, dateTime, useSession } from "../api";
import PeriodField from "./PeriodField.vue";
import ScheduleFields from "./ScheduleFields.vue";
import { metricUnit } from "../queryPresentation";
const deliveryStatus: Record<string, string> = {
  sent: "Sendt",
  failed: "Kunne ikke sendes",
  pending: "Afventer",
  sending: "Sender",
  unknown: "Status ukendt",
  cancelled: "Annulleret",
};
const props = defineProps<{ dataset: string; views: any[]; fields: Field[] }>();
const base = "/api/datasets/" + props.dataset,
  session = useSession();
const rules = ref<any[]>([]),
  deliveries = ref<any[]>([]),
  adding = ref(false),
  loading = ref(true),
  error = ref(""),
  message = ref(""),
  busy = ref(false),
  preview = ref<any>(null),
  name = ref(""),
  kind = ref("threshold"),
  operator = ref("lt"),
  threshold = ref("50"),
  email = ref(false),
  cooldown = ref(60),
  stale = ref(1440),
  cron = ref("0 9 * * *"),
  timezone = ref("Europe/Copenhagen"),
  period = ref<any>(),
  metric = ref("sum"),
  field = ref(""),
  savedView = ref("");
const numerics = computed(() =>
  props.fields.filter(
    (f) =>
      f.active &&
      f.queryable &&
      ["integer", "decimal", "rating"].includes(f.type),
  ),
);
const fieldLabel = computed(
  () => props.fields.find((f) => f.id === field.value)?.label ?? "værdien",
);
const unit = computed(() =>
  metricUnit(
    savedView.value
      ? props.views.find((v) => v.id === savedView.value)?.query?.metrics[0]
      : { op: metric.value, fieldId: field.value },
    props.fields,
  ),
);
const operators: Record<string, string> = {
  lt: "under",
  lte: "højst",
  gt: "over",
  gte: "mindst",
};
const measurement = computed(() =>
  savedView.value
    ? props.views.find((v) => v.id === savedView.value)?.name
    : metric.value === "count_records"
      ? "Antallet af rækker"
      : `${metric.value === "avg" ? "Gennemsnittet af" : "Samlet"} ${fieldLabel.value.toLowerCase()}`,
);
const summary = computed(() =>
  kind.value === "threshold"
    ? `${measurement.value} er ${operators[operator.value]} ${threshold.value}${unit.value ? " " + unit.value : ""}`
    : kind.value === "stale"
      ? `Der er ikke kommet nye data i ${stale.value >= 1440 ? stale.value / 1440 + " døgn" : stale.value / 60 + " timer"}`
      : "En import af data mislykkes",
);
function body() {
  const view = props.views.find((v) => v.id === savedView.value);
  return {
    name: name.value.trim() || summary.value.slice(0, 120),
    kind: kind.value,
    definition: {
      ...(kind.value === "threshold"
        ? {
            query:
              view?.query ??
              querySchema.parse({
                metrics: [
                  {
                    op: metric.value,
                    fieldId:
                      metric.value === "count_records"
                        ? undefined
                        : field.value,
                    alias: "resultat",
                  },
                ],
              }),
            metricAlias: view?.query?.metrics[0]?.alias ?? "resultat",
            operator: operator.value,
            threshold: threshold.value.replace(",", "."),
          }
        : {}),
      cooldownMinutes: Number(cooldown.value),
      staleMinutes: Number(stale.value),
      email: email.value,
      ...(period.value
        ? { window: { ...period.value, timezone: timezone.value } }
        : view?.config?.window
          ? { window: view.config.window }
          : {}),
    },
  };
}
watch(
  [
    name,
    kind,
    operator,
    threshold,
    email,
    cooldown,
    stale,
    cron,
    timezone,
    period,
    metric,
    field,
    savedView,
  ],
  () => (preview.value = null),
  { deep: true },
);
async function load() {
  try {
    [rules.value, deliveries.value] = await Promise.all([
      api(base + "/automations"),
      api(base + "/deliveries"),
    ]);
  } catch (e) {
    error.value = errorText(e);
  } finally {
    loading.value = false;
  }
}
onMounted(load);
function create() {
  name.value = "";
  kind.value = "threshold";
  field.value = numerics.value[0]?.id ?? "";
  metric.value = field.value ? "sum" : "count_records";
  savedView.value = "";
  period.value = undefined;
  preview.value = null;
  error.value = "";
  message.value = "";
  adding.value = true;
}
async function action(fn: () => Promise<void>) {
  busy.value = true;
  error.value = "";
  message.value = "";
  try {
    await fn();
  } catch (e) {
    error.value = errorText(e);
  } finally {
    busy.value = false;
  }
}
async function inspect() {
  await action(
    async () =>
      (preview.value = await api(base + "/automations/preview", {
        method: "POST",
        body: body(),
      })),
  );
}
async function activate() {
  await action(async () => {
    await api(base + "/automations", {
      method: "POST",
      body: {
        ...preview.value.definition,
        schedule: { cron: cron.value, timezone: timezone.value },
      },
    });
    adding.value = false;
    preview.value = null;
    message.value =
      "Reglen er gemt. Vi giver besked, når betingelsen bliver opfyldt.";
    await load();
  });
}
</script>
<template>
  <div class="section-heading">
    <div>
      <h2>Få besked, når noget ændrer sig</h2>
      <p class="muted">
        Vælg det, du vil følge. Vi holder øje og giver dig besked i indbakken.
      </p>
    </div>
    <button class="button primary" @click="create">
      <AddIcon :size="17" />Ny regel
    </button>
  </div>
  <p v-if="error" class="error" role="alert">{{ error }}</p>
  <p v-if="message" class="notice" role="status">{{ message }}</p>
  <form v-if="adding" class="rule-editor form-stack" @submit.prevent="inspect">
    <fieldset :disabled="busy" class="rule-fields">
      <label
        >Hvad vil du have besked om?<select v-model="kind">
          <option value="threshold">En værdi når en grænse</option>
          <option value="import_failure">Data kunne ikke hentes</option>
          <option value="stale">Der er ikke kommet nye data</option>
        </select></label
      >
      <template v-if="kind === 'threshold'">
        <div class="form-grid">
          <label
            >Følg<select v-model="metric" @change="savedView = ''">
              <option value="sum" :disabled="!numerics.length">
                Samlet værdi
              </option>
              <option value="avg" :disabled="!numerics.length">
                Gennemsnit
              </option>
              <option value="count_records">Antal rækker</option>
            </select></label
          ><label v-if="metric !== 'count_records'"
            >Af<select v-model="field" required @change="savedView = ''">
              <option v-for="f in numerics" :key="f.id" :value="f.id">
                {{ f.label }}{{ f.unit ? " · " + f.unit : "" }}
              </option>
            </select></label
          >
        </div>
        <div class="form-grid">
          <label
            >Giv besked, når værdien er<select v-model="operator">
              <option v-for="(label, key) in operators" :key="key" :value="key">
                {{ label }}
              </option>
            </select></label
          ><label
            >Grænse{{ unit ? " i " + unit : ""
            }}<input
              v-model="threshold"
              required
              inputmode="decimal"
              pattern="-?[0-9]+([.,][0-9]+)?"
          /></label>
        </div>
        <PeriodField v-model="period" :fields="fields" />
        <details
          v-if="
            views.some(
              (v) =>
                v.query.metrics?.length === 1 && !v.query.dimensions?.length,
            )
          "
        >
          <summary>Brug en gemt måling</summary>
          <label
            >Dashboardkort<select v-model="savedView">
              <option value="">Brug valgene ovenfor</option>
              <option
                v-for="v in views.filter(
                  (v) =>
                    v.query.metrics?.length === 1 &&
                    !v.query.dimensions?.length,
                )"
                :key="v.id"
                :value="v.id"
              >
                {{ v.name }}
              </option>
            </select></label
          >
        </details>
      </template>
      <label v-if="kind === 'stale'"
        >Giv besked efter<select v-model="stale">
          <option :value="60">1 time uden nye data</option>
          <option :value="360">6 timer uden nye data</option>
          <option :value="1440">1 døgn uden nye data</option>
          <option :value="10080">1 uge uden nye data</option>
        </select></label
      >
      <div class="rule-summary">
        <BellIcon :size="19" />
        <p><strong>Giv mig besked, når</strong><br />{{ summary }}.</p>
      </div>
      <section class="rule-schedule">
        <h3>Hvornår skal vi tjekke?</h3>
        <ScheduleFields
          v-model="cron"
          v-model:timezone="timezone"
          :disabled="busy"
        />
      </section>
      <label
        >Reglens navn<input
          v-model="name"
          :placeholder="summary"
          maxlength="120"
      /></label>
      <label class="checkbox"
        ><input
          v-model="email"
          type="checkbox"
          :disabled="!session.capabilities.email"
        />Send også besked på email</label
      >
      <p v-if="!session.capabilities.email" class="muted small">
        Beskeder kommer i din indbakke. Email er ikke sat op endnu.
      </p>
      <details>
        <summary>Gentagne beskeder</summary>
        <p class="muted small">
          Vi giver besked, når betingelsen bliver opfyldt. En ny besked kræver,
          at værdien først er tilbage i orden.
        </p>
        <label
          >Vent mindst mellem beskeder<select v-model="cooldown">
            <option :value="0">Ingen ekstra ventetid</option>
            <option :value="60">1 time</option>
            <option :value="1440">1 døgn</option>
          </select></label
        >
      </details>
    </fieldset>
    <div v-if="preview" class="notice rule-preview" role="status">
      <h3>Sådan ser det ud lige nu</h3>
      <p v-if="preview.result">
        <strong>{{
          formatDecimal(
            preview.result.rows[0]?.[preview.definition.definition.metricAlias],
          )
        }}</strong>
        {{ unit }} · {{ preview.result.metadata.matchedRecords }} rækker i
        perioden.
      </p>
      <p v-else>{{ summary }}.</p>
      <p class="small">
        Du får besked i indbakken{{ email ? " og på email" : "" }}. Hvis ingen
        data matcher, udløses der ingen alarm om en grænseværdi.
      </p>
    </div>
    <div class="form-actions">
      <button
        type="button"
        class="button secondary"
        :disabled="busy"
        @click="adding = false"
      >
        Annullér</button
      ><button v-if="!preview" class="button primary" :disabled="busy">
        {{ busy ? "Tjekker…" : "Se hvordan reglen virker" }}</button
      ><button
        v-else
        type="button"
        class="button primary"
        :disabled="busy"
        @click="activate"
      >
        {{ busy ? "Gemmer…" : "Aktivér regel" }}
      </button>
    </div>
  </form>
  <p v-if="loading" class="empty-state" role="status">Henter dine regler…</p>
  <div v-else-if="!rules.length && !adding" class="empty-state">
    <BellIcon :size="28" />
    <h3>Du behøver ikke holde øje selv</h3>
    <p>
      Få fx besked, når ugens distance er under dit mål, eller nye data
      udebliver.
    </p>
    <button class="button secondary" @click="create">
      Lav din første regel
    </button>
  </div>
  <article v-for="r in rules" :key="r.id" class="rule-row">
    <div>
      <h3>{{ r.name }}</h3>
      <p class="muted">
        {{
          r.kind === "threshold"
            ? "Følger en måling"
            : r.kind === "stale"
              ? "Holder øje med nye data"
              : "Holder øje med import"
        }}
        · Sidst tjekket {{ dateTime(r.last_evaluated_at) }}
      </p>
    </div>
    <span
      class="status-pill"
      :class="
        r.state === 'alert'
          ? 'warning'
          : r.state === 'ok'
            ? 'success'
            : 'neutral'
      "
      >{{
        !r.enabled
          ? "Sat på pause"
          : r.state === "alert"
            ? "Betingelsen er opfyldt"
            : r.state === "ok"
              ? "Alt i orden"
              : "Afventer første tjek"
      }}</span
    ><button
      class="button secondary"
      :disabled="busy || !r.enabled"
      @click="
        action(async () => {
          await api(base + '/automations/' + r.id + '/run', { method: 'POST' });
          message = 'Reglen bliver tjekket.';
        })
      "
    >
      <PlayIcon :size="16" />Tjek nu</button
    ><button
      class="text-button"
      :disabled="busy"
      @click="
        action(async () => {
          await api(base + '/automations/' + r.id, {
            method: 'PATCH',
            body: { enabled: !r.enabled },
          });
          await load();
        })
      "
    >
      {{ r.enabled ? "Sæt på pause" : "Genoptag" }}
    </button>
  </article>
  <details v-if="deliveries.length">
    <summary>Sendte emails</summary>
    <div v-for="d in deliveries" :key="d.id" class="delivery-row">
      <strong>{{ d.subject }}</strong
      ><span class="status-pill neutral">{{
        deliveryStatus[d.status] ?? d.status
      }}</span>
      <p v-if="d.error">{{ d.error }}</p>
    </div>
  </details>
</template>
<style
  scoped
  lang="scss"
  src="../styles/components/AutomationPanel.scss"
></style>
